import React, { useEffect, useState } from "react";
import ImageWithBasePath from "../../../core/common/imageWithBasePath";
import { Link } from "react-router-dom";
import CollapseHeader from "../../../core/common/collapse-header";
import { toast } from "react-toastify";
import axios from "axios";
import { API_BASE_PATH } from "../../../environment";
import {
  createMetaIntegration,
  updateMetaIntegration,
  getMetaIntegration,
  fetchPageLeadData,
  syncContactsFromMeta,
  fetchPageLeads,
  syncLeadsAllPages,
  MetaIntegrationStatus,
  LeadForm,
  LeadDataResponse,
} from "../../../api/metaapi";

interface Integration {
  id: string;
  name: string;
  icon: string;
  isImage: boolean;
  status: string;
  description: string;
}

const MetaSettings = () => {
  const [metaIntegration, setMetaIntegration] = useState<MetaIntegrationStatus | null>(null);
  const [leadForms, setLeadForms] = useState<LeadForm[]>([]);
  const [leadData, setLeadData] = useState<LeadDataResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [syncLoading, setSyncLoading] = useState(false);
  const [formData, setFormData] = useState({
    access_token: "",
    ad_account_id: "",
    api_base_url: "",
    api_version: "",
    app_id: "",
    app_secret: "",
    test_ad_account_id: "",
  });

  const integrations: Integration[] = [
    {
      id: "meta",
      name: "Meta Integration",
      icon: "Meta.svg",
      isImage: true,
      // status: metaIntegration?.active ? "Connected" : "Not Connected",
      status:"Connected",
      description:
        "Meta's API enables integration with Facebook and Instagram for advertising, messaging, and analytics.",
    },
    {
      id: "google_ads",
      name: "Google Ads",
      icon: "google.png",
      isImage: true,
      status: "Not Connected",
      description: "Google Ads API for managing ad campaigns and analytics.",
    },
    {
      id: "linkedin_ads",
      name: "LinkedIn Ads",
      icon: "linkedin.jpg",
      isImage: true,
      status: "Not Connected",
      description: "LinkedIn Ads API for professional advertising and lead generation.",
    },
    {
      id: "twitter_ads",
      name: "Twitter Ads",
      icon: "X.jpeg",
      isImage: true,
      status: "Not Connected",
      description: "Twitter Ads API for promoting content and engaging audiences.",
    },
    {
      id: "tiktok_ads",
      name: "TikTok Ads",
      icon: "tiktok.jpg",
      isImage: true,
      status: "Not Connected",
      description: "TikTok Ads API for creative advertising and audience engagement.",
    },
  ];

  useEffect(() => {
    fetchMetaIntegration();
    fetchLeadsAndData();
  }, []);

  const fetchMetaIntegration = async () => {
    try {
      setLoading(true);
      const response = await getMetaIntegration();
      setMetaIntegration(response);
      setFormData({
        access_token: response.access_token || "",
        ad_account_id: response.ad_account_id || "",
        api_base_url: response.api_base_url || "",
        api_version: response.api_version || "",
        app_id: response.app_id || "",
        app_secret: response.app_secret || "",
        test_ad_account_id: "",
      });
    } catch (error) {
      // Error handled by API function
    } finally {
      setLoading(false);
    }
  };

  const fetchLeadsAndData = async () => {
    if (metaIntegration?.active) {
      try {
        const [leadsResponse, dataResponse] = await Promise.all([
          fetchPageLeads(),
          fetchPageLeadData(),
        ]);
        setLeadForms(leadsResponse.lead_forms);
        setLeadData(dataResponse);
      } catch (error) {
        // Error handled by API function
      }
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    try {
      if (metaIntegration?.active) {
        await updateMetaIntegration(
          formData.access_token,
          formData.ad_account_id,
          formData.api_base_url,
          formData.api_version,
          formData.app_id,
          formData.app_secret
        );
      } else {
        await createMetaIntegration(
          formData.access_token,
          formData.ad_account_id,
          formData.api_base_url,
          formData.api_version,
          formData.app_id,
          formData.app_secret
        );
      }
      await fetchMetaIntegration();
    } catch (err) {
      // Error handled by API function
    }
  };

  const handleTestSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    try {
      await axios.post(
        `${API_BASE_PATH}/integration/meta/test`,
        { ad_account_id: formData.test_ad_account_id },
        {
          headers: {
            Authorization: localStorage.getItem("authToken") || "",
            Accept: "application/json",
          },
        }
      );
      toast.success("Meta integration test successful");
      setFormData((prev) => ({ ...prev, test_ad_account_id: "" }));
    } catch (error) {
      toast.error("Failed to test Meta integration");
    }
  };

  const handleSyncContacts = async () => {
    try {
      setSyncLoading(true);
      await syncContactsFromMeta();
      await fetchLeadsAndData();
    } catch (error) {
      // Error handled by API function
    } finally {
      setSyncLoading(false);
    }
  };

  const handleSyncAllPages = async () => {
    try {
      setSyncLoading(true);
      await syncLeadsAllPages();
      await fetchLeadsAndData();
    } catch (error) {
      // Error handled by API function
    } finally {
      setSyncLoading(false);
    }
  };

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="row">
          <div className="col-md-12">
            <div className="page-header">
              <div className="row align-items-center">
                <div className="col-8">
                  <h4 className="page-title">
                    <i className="ti ti-brand-meta text-primary me-2" />
                    IATT Integrations
                  </h4>
                </div>
                <div className="col-4 text-end">
                  <div className="head-icons">
                    <CollapseHeader />
                  </div>
                </div>
              </div>
            </div>
            <div className="card">
              <div className="card-body">
                <div className="row p-2 m-2">
                  <h4>Integration Providers</h4>
                  {loading ? (
                    <div className="d-flex align-items-center gap-2">
                      <div
                        className="spinner-border text-primary"
                        role="status"
                        style={{ width: "1.5rem", height: "1.5rem" }}
                      >
                        <span className="visually-hidden">Loading...</span>
                      </div>
                      <span>Loading integrations...</span>
                    </div>
                  ) : (
                    integrations.map((provider) => (
                      <div className="col-md-6 mt-2" key={provider.id}>
                        <div className="border rounded p-3 mb-4 d-flex align-items-center">
                          <span className="avatar avatar-lg border me-2 flex-shrink-0">
                            {provider.isImage ? (
                              <ImageWithBasePath
                                src={`assets/img/icons/${provider.icon}`} // Ensure path is correct
                                className="w-100 h-100" // Ensure image fills the avatar
                                alt={provider.name}
                                //onError={(e) => { e.currentTarget.style.display = 'none'; }} // Fallback for missing images
                              />
                            ) : (
                              <i className={`bi ${provider.icon} fs-3`} />
                            )}
                          </span>
                          <div className="flex-grow-1">
                            <h6 className="fw-medium mb-1">{provider.name}</h6>
                            <Link
                              to="#"
                              className={`badge bg-soft-${provider.status === "Connected" ? "success" : "danger"}`}
                            >
                              {provider.status}
                            </Link>
                          </div>
                          <div className="ms-auto">
                            <Link
                              to="#"
                              className="me-3 text-default"
                              data-bs-toggle="collapse"
                              data-bs-target={`#${provider.id}_collapse`}
                            >
                              <i className="ti ti-info-circle-filled me-1" />
                            </Link>
                            <Link
                              to="#"
                              className={`btn btn-${provider.status === "Connected" ? "light" : "primary"}`}
                              data-bs-toggle="modal"
                              data-bs-target={`#${provider.id}_modal`}
                            >
                              <i className="ti ti-tool me-1" />
                              {provider.status === "Connected" ? "View Integration" : "Connect Now"}
                            </Link>
                          </div>
                          <div className="status-toggle ms-3">
                            <input
                              type="checkbox"
                              id={provider.id}
                              className="check"
                              checked={provider.status === "Connected"}
                              disabled
                            />
                            <label htmlFor={provider.id} className="checktoggle" />
                          </div>
                        </div>
                        <div className="collapse" id={`${provider.id}_collapse`}>
                          <div className="mail-collapse">
                            <p>{provider.description}</p>
                            {provider.id === "meta" && metaIntegration && (
                              <>
                                <p><strong>Page ID:</strong> {metaIntegration.page_id || "N/A"}</p>
                                <p>
                                  <strong>Page Access Token:</strong>{" "}
                                  {metaIntegration.page_access_token
                                    ? `${metaIntegration.page_access_token.substring(0, 20)}...`
                                    : "N/A"}
                                </p>
                                <p><strong>Token Expiry:</strong> {metaIntegration.token_expiry || "N/A"}</p>
                                <p><strong>Active:</strong> {metaIntegration.active ? "Yes" : "No"}</p>
                                <p><strong>App ID:</strong> {metaIntegration.app_id || "N/A"}</p>
                                {leadData && (
                                  <div>
                                    <p><strong>Leads Fetched:</strong> {leadData.leads_fetched}</p>
                                    <p><strong>Contacts Created:</strong> {leadData.contacts_created}</p>
                                  </div>
                                )}
                                {leadForms.length > 0 && (
                                  <div>
                                    <h6>Lead Forms:</h6>
                                    <ul>
                                      {leadForms.map((form) => (
                                        <li key={form.id}>
                                          {form.name} ({form.locale}) - {form.status}
                                        </li>
                                      ))}
                                    </ul>
                                  </div>
                                )}
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Modals for Integration Settings */}
      {integrations.map((provider) => (
        <div className="modal fade" id={`${provider.id}_modal`} role="dialog" key={provider.id}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header d-flex align-items-center gap-2">
                {provider.isImage ? (
                  <ImageWithBasePath
                    src={`assets/img/icons/${provider.icon}`} // Ensure path is correct
                    className="w-auto h-auto"
                    alt={provider.name}
                    //onError={(e) => { e.currentTarget.style.display = 'none'; }} // Fallback for missing images
                  />
                ) : (
                  <i className={`bi ${provider.icon} fs-3 text-primary`} />
                )}
                <h5 className="modal-title mb-0">{provider.name} Settings</h5>
                <button
                  className="btn-close custom-btn-close border p-1 me-0 text-dark"
                  data-bs-dismiss="modal"
                  aria-label="Close"
                >
                  <i className="ti ti-x" />
                </button>
              </div>
              {provider.id === "meta" ? (
                <form onSubmit={handleSubmit}>
                  <div className="modal-body">
                    <div className="mb-3">
                      <label className="col-form-label">
                        Access Token <span className="text-danger">*</span>
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        name="access_token"
                        value={formData.access_token}
                        onChange={handleInputChange}
                      />
                    </div>
                    <div className="mb-3">
                      <label className="col-form-label">
                        Ad Account ID <span className="text-danger">*</span>
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        name="ad_account_id"
                        value={formData.ad_account_id}
                        onChange={handleInputChange}
                      />
                    </div>
                    <div className="mb-3">
                      <label className="col-form-label">
                        API Base URL <span className="text-danger">*</span>
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        name="api_base_url"
                        value={formData.api_base_url}
                        onChange={handleInputChange}
                      />
                    </div>
                    <div className="mb-3">
                      <label className="col-form-label">
                        API Version <span className="text-danger">*</span>
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        name="api_version"
                        value={formData.api_version}
                        onChange={handleInputChange}
                      />
                    </div>
                    <div className="mb-3">
                      <label className="col-form-label">
                        App ID <span className="text-danger">*</span>
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        name="app_id"
                        value={formData.app_id}
                        onChange={handleInputChange}
                      />
                    </div>
                    <div className="mb-0">
                      <label className="col-form-label">
                        App Secret <span className="text-danger">*</span>
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        name="app_secret"
                        value={formData.app_secret}
                        onChange={handleInputChange}
                      />
                    </div>
                  </div>
                  <div className="modal-footer">
                    <Link to="#" className="btn btn-light me-2" data-bs-dismiss="modal">
                      Cancel
                    </Link>
                    <button type="submit" className="btn btn-primary" data-bs-dismiss="modal">
                      Save
                    </button>
                  </div>
                </form>
              ) : (
                <div className="modal-body">
                  <p className="text-muted">
                    {provider.name} integration is coming soon.
                    This is a showcase of how multiple providers will be listed here.
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

export default MetaSettings;